// ============================================================
// formatHelpers.js
// Universal content formatting and sanitization pipeline.
// All format manipulation/fixing passes live here.
// ============================================================

// --------------- Internal: shared primitives ---------------

const EMPTY_PARAGRAPH_HTML_RE = /<p(?:\s+[^>]*)?>(?:\s|&nbsp;|&#160;|<br\s*\/?\s*>)*<\/p>/gi;
const HTML_COMMENT_RE = /<!--[\s\S]*?-->/g;
const DANGEROUS_BLOCK_TAG_RE = /<(script|style|iframe|object|embed|applet|svg|math|meta|link|base|form|input|button|textarea|select|option)\b[^>]*>[\s\S]*?<\/\1>/gi;
const DANGEROUS_SELF_CLOSING_TAG_RE = /<(script|style|iframe|object|embed|applet|svg|math|meta|link|base|form|input|button|textarea|select|option)\b[^>]*\/?>/gi;
const EVENT_HANDLER_ATTR_RE = /\son[a-z0-9_-]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi;
const XMLNS_ATTR_RE = /\sxmlns(?::[a-z0-9_-]+)?\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi;
const SAFE_INLINE_STYLE_PROPS = new Set(['font-size', 'font-weight']);

const normalizeNewlines = (value = '') => String(value || '').replace(/\r\n?/g, '\n');

function stripControlAndWhitespaceForProtocol(value = '') {
  let out = '';
  const source = String(value || '');
  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i];
    const code = source.charCodeAt(i);
    const isAsciiControl = (code >= 0 && code <= 31) || code === 127;
    const isWhitespace = /\s/.test(ch);
    if (isAsciiControl || isWhitespace) continue;
    out += ch;
  }
  return out;
}

function stripUnsafeTextControls(value = '') {
  let out = '';
  const source = String(value || '');
  for (let i = 0; i < source.length; i += 1) {
    const code = source.charCodeAt(i);
    const isBlocked =
      (code >= 0 && code <= 8)
      || code === 11
      || code === 12
      || (code >= 14 && code <= 31)
      || code === 127;
    if (isBlocked) continue;
    out += source[i];
  }
  return out;
}

function hasUnsafeUrlProtocol(value = '') {
  const jsProtocol = ['java', 'script:'].join('');
  const raw = String(value || '')
    .replace(/&(?:#x0*3a|#0*58|colon);?/gi, ':')
    .replace(/\u200B/g, '');
  const normalized = stripControlAndWhitespaceForProtocol(raw).toLowerCase();

  return normalized.startsWith(jsProtocol)
    || normalized.startsWith('vbscript:')
    || normalized.startsWith('data:')
    || normalized.startsWith('file:');
}

function sanitizeUrlAttributesInHtml(html = '') {
  return String(html || '').replace(
    /(\s(?:href|src|xlink:href|formaction|poster)\s*=\s*)("[^"]*"|'[^']*'|[^\s>]+)/gi,
    (match, prefix, rawValue) => {
      const quote = rawValue.startsWith('"') ? '"' : (rawValue.startsWith("'") ? "'" : '');
      const unquoted = quote ? rawValue.slice(1, -1) : rawValue;
      if (!hasUnsafeUrlProtocol(unquoted)) return `${prefix}${rawValue}`;
      return `${prefix}${quote}#${quote}`;
    }
  );
}

function sanitizeStyleAttributesInHtml(html = '') {
  return String(html || '').replace(
    /(\sstyle\s*=\s*)("([^"]*)"|'([^']*)'|([^\s>]+))/gi,
    (match, prefix, rawValue, doubleQuoted, singleQuoted, unquoted) => {
      const styleValue = String(doubleQuoted ?? singleQuoted ?? unquoted ?? '');
      const cleanDeclarations = styleValue
        .split(';')
        .map(part => part.trim())
        .filter(Boolean)
        .map((decl) => {
          const colonIndex = decl.indexOf(':');
          if (colonIndex <= 0) return null;

          const prop = decl.slice(0, colonIndex).trim().toLowerCase();
          const val = decl.slice(colonIndex + 1).trim();
          if (!SAFE_INLINE_STYLE_PROPS.has(prop)) return null;
          if (!val) return null;
          if (/(?:expression\s*\(|url\s*\(|javascript:|vbscript:|@import|behavior\s*:)/i.test(val)) {
            return null;
          }

          return `${prop}: ${val}`;
        })
        .filter(Boolean);

      if (!cleanDeclarations.length) return '';
      return `${prefix}"${cleanDeclarations.join('; ')}"`;
    }
  );
}

export function sanitizeUntrustedText(text = '') {
  return stripUnsafeTextControls(normalizeNewlines(String(text || '')))
    .replace(/\u2028|\u2029/g, ' ');
}

export function sanitizeUntrustedHtml(html = '') {
  let next = String(html || '');
  if (!next) return '';

  next = next.replace(HTML_COMMENT_RE, '');
  next = next.replace(DANGEROUS_BLOCK_TAG_RE, '');
  next = next.replace(DANGEROUS_SELF_CLOSING_TAG_RE, '');
  next = next.replace(EVENT_HANDLER_ATTR_RE, '');
  next = sanitizeStyleAttributesInHtml(next);
  next = next.replace(XMLNS_ATTR_RE, '');
  next = sanitizeUrlAttributesInHtml(next);

  return next;
}

/**
 * Shared input sanitizer for caller-facing entry points.
 * Use this when normalizing non-editor inputs (chat/title/ids/html payloads)
 * so all code paths apply the same baseline protections.
 */
export function sanitizeInputByType(value = '', type = 'text', options = {}) {
  const normalizedType = String(type || 'text').trim().toLowerCase();
  const maxLength = Number(options?.maxLength || 0);

  const applyLengthCap = (next) => {
    if (!Number.isFinite(maxLength) || maxLength <= 0) return next;
    return String(next || '').slice(0, maxLength);
  };

  if (normalizedType === 'html') {
    return applyLengthCap(sanitizeUntrustedHtml(value));
  }

  if (normalizedType === 'id' || normalizedType === 'identifier' || normalizedType === 'key') {
    const safe = sanitizeUntrustedText(value)
      .trim()
      .replace(/[^a-zA-Z0-9._:-]/g, '');
    return applyLengthCap(safe);
  }

  if (normalizedType === 'title') {
    const safe = sanitizeUntrustedText(value)
      .replace(/\s+/g, ' ')
      .trim();
    return applyLengthCap(safe);
  }

  if (normalizedType === 'chat') {
    const safe = sanitizeUntrustedText(value)
      .replace(/\u200B/g, '');
    return applyLengthCap(safe);
  }

  const safe = sanitizeUntrustedText(value);
  return applyLengthCap(safe);
}

/**
 * Recursively sanitizes nested JSON-like data (objects/arrays/strings).
 * Useful for metadata/visual-state blobs where strings may appear deeply
 * nested and bypass top-level sanitization.
 */
export function sanitizeStructuredInput(value, options = {}) {
  const maxDepth = Math.max(1, Number(options?.maxDepth || 8));
  const maxStringLength = Math.max(1, Number(options?.maxStringLength || 4000));
  const seen = new WeakSet();

  const walk = (node, depth) => {
    if (depth > maxDepth) return null;
    if (node === null) return null;

    const nodeType = typeof node;

    if (nodeType === 'string') {
      return sanitizeInputByType(node, 'text', { maxLength: maxStringLength });
    }

    if (nodeType === 'number') {
      return Number.isFinite(node) ? node : null;
    }

    if (nodeType === 'boolean') return node;

    if (nodeType === 'bigint') {
      return sanitizeInputByType(String(node), 'text', { maxLength: maxStringLength });
    }

    if (nodeType !== 'object') return null;

    if (node instanceof Date) {
      return Number.isNaN(node.getTime()) ? null : node.toISOString();
    }

    if (seen.has(node)) return null;
    seen.add(node);

    if (Array.isArray(node)) {
      return node.map((item) => walk(item, depth + 1));
    }

    const out = {};
    for (const [key, val] of Object.entries(node)) {
      if (key === '__proto__' || key === 'constructor' || key === 'prototype') continue;
      const safeVal = walk(val, depth + 1);
      if (safeVal !== undefined) {
        out[key] = safeVal;
      }
    }

    return out;
  };

  return walk(value, 0);
}

const collapseAlternatingBlankLines = (value = '') => {
  const normalized = normalizeNewlines(value);
  const lines = normalized.split('\n');
  if (lines.length < 4) return normalized;

  const looksAlternating = lines.every((line, index) => {
    if (index % 2 === 1) return line.trim() === '';
    return line.trim() !== '';
  });

  if (!looksAlternating) return normalized;
  return lines.filter((_, index) => index % 2 === 0).join('\n');
};

// --------------- Feature flag ---------------

// Set to false to skip structural normalization passes.
// Base sanitization (normalizeAiResponseText, sanitizePastedHtml, sanitizePastedText) always runs.
const CONTENT_FORMATTING_ENABLED = true;

// --------------- Pass 1: Base sanitization ---------------

export const normalizeAiResponseText = (value = '') => {
  let next = sanitizeUntrustedText(value)
    .replace(/\u200B/g, '')
    .replace(/\t/g, '  ')
    .trim();

  if (!next) return '';

  // Handle providers that emit HTML paragraph wrappers.
  next = next.replace(EMPTY_PARAGRAPH_HTML_RE, '\n\n');
  next = next
    .replace(/<p(?:\s+[^>]*)?>/gi, '')
    .replace(/<\/p>/gi, '\n\n');

  next = collapseAlternatingBlankLines(next);
  next = next.replace(/\n{3,}/g, '\n\n').trim();
  return next;
};

export const sanitizePastedHtml = (html = '') => {
  let next = sanitizeUntrustedHtml(html);
  if (!next) return '';

  // External sources (e.g. Wikipedia) often emit &nbsp; between inline tags.
  // Convert those to normal spaces so editor text/undo logic does not carry
  // non-breaking-space artifacts into suggestion diffs.
  next = next
    .replace(/\u00A0/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ');

  next = next.replace(EMPTY_PARAGRAPH_HTML_RE, '');
  next = next.replace(/(?:\s*<br\s*\/?\s*>\s*){3,}/gi, '<br><br>');
  next = next.replace(/ {3,}/g, '  ');
  return next;
};

export const sanitizePastedText = (text = '') => {
  let next = sanitizeUntrustedText(text);
  next = collapseAlternatingBlankLines(next);
  next = next.replace(/\n{3,}/g, '\n\n');
  return next;
};

function expandInlineOrderedListMarkers(text = '') {
  const raw = String(text || '');
  const markerCount = (raw.match(/\d{1,2}[.)]\s+/g) || []).length;
  if (markerCount < 1) return raw;

  const hasListLikeBoundary = /(?:[:.!?]["'”’')\]]?\s*\d{1,2}[.)]\s+(?:<strong>|<b>|["'“”A-Za-z]))/.test(raw)
    || /(?:^|\s)\d{1,2}[.)]\s+<strong>/i.test(raw)
    || /(?:^|\s)\d{1,2}[.)]\s+<b>/i.test(raw);

  // Keep single-marker prose untouched unless it strongly resembles a flattened list segment.
  if (markerCount === 1 && !hasListLikeBoundary) return raw;

  let next = raw;

  // Common flattening shape: "intro:1. Item...2. Item..."
  next = next.replace(/(:)\s*(\d{1,2}[.)]\s+)/g, '$1\n$2');

  // Also split markers that are glued to sentence endings (e.g. ..."Point."2. Next).
  next = next.replace(
    /([.!?]["'”’')\]]?)\s*(\d{1,2}[.)]\s+(?=(?:<strong>|<b>|["'“”A-Za-z])))/g,
    '$1\n$2'
  );

  // Split inline numbered markers into new lines when they appear mid-line.
  next = next.replace(
    /([^\n])\s+(\d{1,2}[.)]\s+(?=(?:<strong>|<b>|["'“”A-Za-z])))/g,
    '$1\n$2'
  );

  return next.replace(/\n{3,}/g, '\n\n');
}

// --------------- Pass 2a: External HTML normalization ---------------

// Returns true when HTML already has semantic block-level elements
// (p, h*, ul, ol, li, blockquote, table). If so, skip flattening.
const SEMANTIC_BLOCK_RE = /<(p|h[1-6]|ul|ol|li|blockquote|table)[\s>/]/i;
function hasSemanticBlockElements(html) {
  return SEMANTIC_BLOCK_RE.test(String(html || ''));
}

// Strips all HTML tags except <br>, keeping text content.
function stripTagsKeepBr(html) {
  return String(html || '')
    .replace(/<br\s*\/?>/gi, '\u200BBRU\u200B')
    .replace(/<("[^"]*"|'[^']*'|[^>])*>/g, '')
    .replace(/\u200BBRU\u200B/g, '<br>');
}

// Map a numeric px font-size to a heading level (h1–h6).
// Uses a common design type scale; sizes above the body threshold get a level.
function fontSizePxToHeadingLevel(px) {
  if (px >= 48) return 1;
  if (px >= 36) return 2;
  if (px >= 28) return 3;
  if (px >= 22) return 4;
  if (px >= 18) return 5;
  if (px >= 14) return 6;
  return null; // at or below typical body size — not a heading
}

// Extract the computed font-size (px) from the first inline-styled element
// in the raw HTML of a segment. Returns null if not found.
function extractFirstFontSizePx(rawHtml) {
  const styleMatch = String(rawHtml || '').match(/style=["'][^"']*font-size:\s*([\d.]+)px/i);
  if (!styleMatch) return null;
  const px = parseFloat(styleMatch[1]);
  return Number.isFinite(px) ? px : null;
}

// Returns true when the segment's first element is wrapped in <b> or <strong>,
// or has font-weight: bold/700+ in its inline style, but no notable font-size increase.
function isOnlyBold(rawSegment, bodyFontSizePx) {
  const s = String(rawSegment || '');
  const hasBoldTag = /<(?:b|strong)[\s>]/i.test(s);
  const hasBoldStyle = /font-weight:\s*(?:bold|[6-9]\d{2})/i.test(s);
  if (!hasBoldTag && !hasBoldStyle) return false;

  // If there's also a meaningful size increase, let size-based detection win.
  const segPx = extractFirstFontSizePx(s);
  if (segPx !== null && bodyFontSizePx !== null && segPx - bodyFontSizePx >= 2) return false;

  return true;
}

// Infer baseline body font size from all segments by taking the most common px
// value (mode) or the median, excluding the first segment (which may be a title).
function inferBodyFontSizePx(segments) {
  const sizes = segments
    .slice(1)
    .map(s => extractFirstFontSizePx(s))
    .filter(n => n !== null);
  if (!sizes.length) return null;
  const freq = {};
  let maxCount = 0, mode = sizes[0];
  for (const n of sizes) {
    freq[n] = (freq[n] || 0) + 1;
    if (freq[n] > maxCount) { maxCount = freq[n]; mode = n; }
  }
  return mode;
}

function isOffsetInsideTagScope(source = '', offset = 0, openTag = 'li') {
  const prefix = String(source || '').slice(0, Math.max(0, offset));
  const openRe = new RegExp(`<${openTag}\\b[^>]*>`, 'gi');
  const closeRe = new RegExp(`</${openTag}>`, 'gi');

  const openCount = (prefix.match(openRe) || []).length;
  const closeCount = (prefix.match(closeRe) || []).length;
  return openCount > closeCount;
}

// Promotes <p> elements that are entirely bold and/or have a larger font-size
// than the inferred body size to semantic heading elements.
// Handles Figma and other design-tool HTML that wraps styled text in <p> tags
// instead of <h1>–<h6>. Runs after normalizeExternalFlatHtml (which only
// handles truly flat HTML) and before the markdown-heading pass.
function normalizeStyledParagraphsToHeadings(html) {
  const src = String(html || '');

  // Infer body font size: mode px value across all <p> elements.
  const allPx = [];
  src.replace(/<p(?:\s[^>]*)?>[\s\S]*?<\/p>/gi, (m) => {
    const px = extractFirstFontSizePx(m);
    if (px !== null) allPx.push(px);
    return m;
  });
  let bodyPx = null;
  if (allPx.length) {
    const freq = {};
    for (const n of allPx) freq[n] = (freq[n] || 0) + 1;
    // Iterate ascending so ties resolve to the smaller (body) size,
    // since heading text is larger than body text.
    let maxCount = 0;
    for (const n of Object.keys(freq).map(Number).sort((a, b) => a - b)) {
      if (freq[n] > maxCount) { maxCount = freq[n]; bodyPx = n; }
    }
  }

  return src.replace(/<p(\s[^>]*)?>(<\/p>|[\s\S]*?)<\/p>/gi, (match, attrs = '', inner = '', offset = 0) => {
    // Keep list item content as paragraph text; promoting inside <li> can
    // create invalid list children for the editor schema and drop bullets.
    if (isOffsetInsideTagScope(src, offset, 'li')) return match;

    const text = plainTextFromHtmlFragment(inner).trim();
    // Skip empty or overly long content — not heading material.
    if (!text || text.length < 2 || text.length > 120) return match;

    // Size-based promotion: any font-size meaningfully larger than body.
    const segPx = extractFirstFontSizePx(inner);
    if (segPx !== null && (bodyPx === null || segPx > bodyPx + 1)) {
      const level = fontSizePxToHeadingLevel(segPx);
      if (level !== null) {
        return `<h${level}>${text}</h${level}>`;
      }
    }

    // Bold-only, heading-shaped paragraph → h6 (semantic heading without size cue).
    if (isOnlyBold(inner, bodyPx) && isLikelyStandaloneHeadingLine(text)) {
      return `<h6>${text}</h6>`;
    }

    return match;
  });
}

// Normalizes "flat" external HTML (Figma, some design tools) that uses
// inline <span> + <br> instead of semantic block elements.
// Converts it to structured HTML using font-size cues where available.
function normalizeExternalFlatHtml(html) {
  if (hasSemanticBlockElements(html)) return html;

  let next = String(html || '');

  // Drop meta/style/script noise.
  next = next.replace(/<meta[^>]*>/gi, '');
  next = next.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  next = next.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');

  // Convert block-level container close tags to double <br> so content segments.
  next = next.replace(/<\/(?:div|section|article|header|footer|figure|aside)>/gi, '<br><br>');
  next = next.replace(/<(?:div|section|article|header|footer|figure|aside)[^>]*>/gi, '');

  // Collapse 3+ br to 2 before segmenting.
  next = next.replace(/(?:\s*<br\s*\/?>\s*){3,}/gi, '<br><br>');

  // Split into segments on double <br>.
  const rawSegments = next
    .split(/(?:\s*<br\s*\/?>\s*){2}/gi)
    .map(s => s.trim())
    .filter(Boolean);

  if (!rawSegments.length) return html;

  // Infer body font size from non-first segments.
  const bodyPx = inferBodyFontSizePx(rawSegments);

  const parts = rawSegments.map((rawSeg, i) => {
    const text = stripTagsKeepBr(rawSeg).replace(/[ \t]{2,}/g, ' ').trim();
    if (!text) return null;

    if (i === 0 && isLikelyStandaloneHeadingLine(text)) {
      const segPx = extractFirstFontSizePx(rawSeg);

      // Size-based heading level detection.
      if (segPx !== null) {
        const level = fontSizePxToHeadingLevel(segPx);
        if (level !== null && (bodyPx === null || segPx > bodyPx)) {
          return `<h${level}>${escapeHtml(text)}</h${level}>`;
        }
      }

      // Bold-only first segment → h6 (removing bold markup; h6 implies semantic weight).
      if (isOnlyBold(rawSeg, bodyPx)) {
        return `<h6>${escapeHtml(text)}</h6>`;
      }

      // No style cues but title-like shape → default to h2.
      const hasProseAfterFirst = rawSegments.slice(1).some((s) => {
        const t = stripTagsKeepBr(s).replace(/<br>/g, ' ').replace(/[ \t]{2,}/g, ' ').trim();
        return t.length >= 45 || /[.!?]["')\]]?$/.test(t);
      });
      if (hasProseAfterFirst) {
        return `<h2>${escapeHtml(text)}</h2>`;
      }
    }

    return `<p>${text}</p>`;
  });

  const result = parts.filter(Boolean).join('');
  return result || html;
}

// --------------- Pass 2b: Structural HTML normalization ---------------

// Converts <p>## Heading Text</p> to <h2>Heading Text</h2> etc.
// Only triggers on unambiguous markdown heading syntax at the start of a paragraph.
function normalizeHeadingsInHtml(html) {
  const normalized = html.replace(
    /<p(?:\s[^>]*)?>[ \t]*(#{1,6})[ \t]+([^<\n]+?)[ \t]*<\/p>/gi,
    (match, hashes, text) => {
      const level = Math.min(hashes.length, 6);
      return `<h${level}>${text.trim()}</h${level}>`;
    }
  );

  return stripBoldInsideHeadingTags(normalized);
}

function stripBoldInsideHeadingTags(html = '') {
  return String(html || '').replace(
    /<h([1-6])(\s[^>]*)?>([\s\S]*?)<\/h\1>/gi,
    (match, level, attrs = '', inner = '') => {
      const withoutBold = String(inner || '').replace(/<\/?(?:strong|b)(?:\s[^>]*)?>/gi, '');
      return `<h${level}${attrs || ''}>${withoutBold}</h${level}>`;
    }
  );
}

function stripBoldMarkersInMarkdownHeadingLines(text = '') {
  return String(text || '').replace(
    /^(#{1,6}\s+)(.+)$/gm,
    (match, prefix, rest) => {
      const clean = String(rest || '')
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/__(.*?)__/g, '$1')
        .replace(/<\/?(?:strong|b)(?:\s[^>]*)?>/gi, '');
      return `${prefix}${clean}`;
    }
  );
}

function plainTextFromHtmlFragment(fragment = '') {
  return String(fragment || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isLikelyStandaloneHeadingLine(line = '') {
  const text = plainTextFromHtmlFragment(line);
  if (!text) return false;
  if (text.length < 4 || text.length > 90) return false;
  if (/^([-*•]|\d+[.)])\s+/.test(text)) return false;
  if (/[.!?:;]["')\]]?$/.test(text)) return false;

  const words = text.split(/\s+/).filter(Boolean);
  if (words.length < 2 || words.length > 10) return false;

  // Bias toward title-like content by requiring at least one alphabetic char.
  return /[A-Za-z]/.test(text);
}

// Handles flattened external content like:
// <p>Title<br><br>Paragraph 1<br><br>Paragraph 2</p>
// Promotes the title-like lead segment to the appropriate heading level.
// Uses font-size from inline styles when available; falls back to bold detection
// (h6) and finally defaults to h2 when no style cues are present.
function normalizeLeadHeadingFromDoubleBreakParagraph(html) {
  return html.replace(/<p(?:[^>]*)>([\s\S]*?)<\/p>/gi, (match, content) => {
    const rawSegments = String(content || '')
      .split(/(?:\s*<br\s*\/?\s*>\s*){2,}/i)
      .map(segment => segment.trim())
      .filter(Boolean);

    if (rawSegments.length < 2) return match;

    const leadRaw = rawSegments[0];
    const leadText = plainTextFromHtmlFragment(leadRaw);
    const rest = rawSegments.slice(1);
    if (!isLikelyStandaloneHeadingLine(leadText)) return match;

    const hasProseAfterLead = rest.some((segment) => {
      const text = plainTextFromHtmlFragment(segment);
      return text.length >= 45 || /[.!?]["')\]]?$/.test(text);
    });
    if (!hasProseAfterLead) return match;

    if (!leadText) return match;

    // Determine heading level from style cues.
    const bodyPx = inferBodyFontSizePx(rawSegments);
    const leadPx = extractFirstFontSizePx(leadRaw);
    let level = 2; // default

    if (leadPx !== null) {
      const sizeLevel = fontSizePxToHeadingLevel(leadPx);
      if (sizeLevel !== null && (bodyPx === null || leadPx > bodyPx)) {
        level = sizeLevel;
      }
    } else if (isOnlyBold(leadRaw, bodyPx)) {
      level = 6;
    }

    const paragraphHtml = rest
      .map(segment => segment.trim())
      .filter(Boolean)
      .map(segment => `<p>${plainTextFromHtmlFragment(segment) || segment}</p>`)
      .join('');

    return `<h${level}>${escapeHtml(leadText)}</h${level}>${paragraphHtml}`;
  });
}

// Converts <p> elements containing br-separated list items into <ul> or <ol>.
// Only triggers when ALL br-split parts match the same list type (balanced conversion).
function normalizeListsInHtml(html) {
  const BULLET_RE = /^(?:[-*•]|&bull;|&#8226;|&#x2022;)\s+(.+)$/i;
  const ORDERED_RE = /^\d+[.)]\s+(.+)$/;
  const BULLET_PARAGRAPH_RUN_RE = /(?:<p(?:[^>]*)>\s*(?:[-*•]|&bull;|&#8226;|&#x2022;)\s+[\s\S]*?<\/p>\s*){2,}/gi;
  const ORDERED_PARAGRAPH_RUN_RE = /(?:<p(?:[^>]*)>\s*\d+[.)]\s+[\s\S]*?<\/p>\s*){2,}/gi;

  let next = html.replace(/<p(?:[^>]*)>([\s\S]*?)<\/p>/gi, (match, content) => {
    const rawParts = content.split(/<br\s*\/?\s*>/i);
    const parts = rawParts
      .map(p => p.replace(/&nbsp;|&#160;/g, ' ').trim())
      .filter(Boolean);

    if (parts.length < 2) return match;

    if (parts.every(p => BULLET_RE.test(p))) {
      const items = parts
        .map(p => `<li>${p.replace(BULLET_RE, '$1')}</li>`)
        .join('');
      return `<ul>${items}</ul>`;
    }

    if (parts.every(p => ORDERED_RE.test(p))) {
      const items = parts
        .map(p => `<li>${p.replace(ORDERED_RE, '$1')}</li>`)
        .join('');
      return `<ol>${items}</ol>`;
    }

    return match;
  });

  next = next.replace(BULLET_PARAGRAPH_RUN_RE, (match) => {
    const paragraphMatches = [...match.matchAll(/<p(?:[^>]*)>([\s\S]*?)<\/p>/gi)];
    if (paragraphMatches.length < 2) return match;

    const parts = paragraphMatches
      .map(([, content]) => plainTextFromHtmlFragment(content).replace(/&nbsp;|&#160;/gi, ' ').trim())
      .filter(Boolean);

    if (parts.length !== paragraphMatches.length) return match;

    const items = parts
      .map(p => `<li>${p.replace(BULLET_RE, '$1')}</li>`)
      .join('');
    return `<ul>${items}</ul>`;
  });

  return next.replace(ORDERED_PARAGRAPH_RUN_RE, (match) => {
    const paragraphMatches = [...match.matchAll(/<p(?:[^>]*)>([\s\S]*?)<\/p>/gi)];
    if (paragraphMatches.length < 2) return match;

    const parts = paragraphMatches
      .map(([, content]) => plainTextFromHtmlFragment(content).replace(/&nbsp;|&#160;/gi, ' ').trim())
      .filter(Boolean);

    if (parts.length !== paragraphMatches.length) return match;

    const items = parts
      .map(p => `<li>${p.replace(ORDERED_RE, '$1')}</li>`)
      .join('');
    return `<ol>${items}</ol>`;
  });
}

// Splits <p> elements that contain double-<br> runs into separate paragraphs.
// <p>Alpha<br><br>Beta</p> → <p>Alpha</p><p>Beta</p>
function normalizeDoubleBrInHtml(html) {
  return html.replace(/<p(?:[^>]*)>([\s\S]*?)<\/p>/gi, (match, content) => {
    const segments = content.split(/(?:\s*<br\s*\/?\s*>\s*){2,}/i);
    if (segments.length < 2) return match;
    return segments
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => `<p>${s}</p>`)
      .join('');
  });
}

// --------------- Pass 3: Text structure detection / conversion ---------------

const HEADING_LINE_RE = /^#{1,6}\s/m;
const BULLET_ITEM_LINE_RE = /^[-*•]\s/m;
const ORDERED_ITEM_LINE_RE = /^\d+[.)]\s/m;
const HTML_LIKE_TAG_RE = /<\/?[a-z][\w:-]*(?:\s[^<>]*)?>/i;
const HTML_LIKE_TAG_GLOBAL_RE = /<\/?[a-z][\w:-]*(?:\s[^<>]*)?>/gi;

/**
 * Returns true when the text contains unambiguous markdown structure patterns
 * (headings, bullet lists, or ordered lists).
 */
export function hasMarkdownStructure(text = '') {
  const t = String(text || '');
  return HEADING_LINE_RE.test(t) || BULLET_ITEM_LINE_RE.test(t) || ORDERED_ITEM_LINE_RE.test(t);
}

/**
 * Returns true when plain text has a title-like first paragraph followed by
 * prose paragraphs — even with no markdown markers.
 * Used to intercept plain-text pastes from external sources like Figma.
 */
export function hasTitleStructure(text = '') {
  const normalized = normalizeNewlines(String(text || '')).replace(/\n{3,}/g, '\n\n').trim();
  if (!normalized) return false;

  const blocks = normalized.split('\n\n').map(b => b.trim()).filter(Boolean);
  if (blocks.length < 2) return false;

  const firstBlock = blocks[0];
  // First block must be a single line (not a multi-line paragraph).
  if (firstBlock.includes('\n')) return false;

  if (!isLikelyStandaloneHeadingLine(firstBlock)) return false;

  // At least one subsequent block must look like prose.
  return blocks.slice(1).some(b => b.length >= 45 || /[.!?]["')\]]?$/.test(b));
}

/**
 * Returns true when plain text appears to contain real HTML markup.
 * Used to decide whether to treat text/plain clipboard content as HTML
 * and insert it as rich content.
 */
export function hasHtmlLikeMarkup(text = '') {
  const value = String(text || '').trim();
  if (!value || !value.includes('<') || !value.includes('>')) return false;
  return HTML_LIKE_TAG_RE.test(value);
}

/**
 * Returns true when the entire clipboard text appears to be HTML content,
 * not prose that merely contains an embedded HTML snippet.
 */
export function isEntireHtmlContent(text = '') {
  const value = String(text || '').trim();
  if (!hasHtmlLikeMarkup(value)) return false;
  return /^<[^>]+>[\s\S]*<\/[^>]+>$/.test(value);
}

function lineToEscapedHtmlWithInlineCodeTags(line = '') {
  const text = String(line || '');
  let out = '';
  let cursor = 0;

  for (const match of text.matchAll(HTML_LIKE_TAG_GLOBAL_RE)) {
    const full = match[0] || '';
    const index = Number.isFinite(match.index) ? match.index : -1;
    if (index < 0) continue;
    out += escapeHtml(text.slice(cursor, index));
    out += `<code>${escapeHtml(full)}</code>`;
    cursor = index + full.length;
  }

  out += escapeHtml(text.slice(cursor));
  return out;
}

/**
 * Converts mixed plain text containing HTML snippets into safe rich HTML:
 * - standalone HTML lines become code blocks
 * - inline HTML snippets become inline code
 */
export function convertMixedHtmlSnippetsToRichHtml(text = '') {
  const value = normalizeNewlines(String(text || ''));
  if (!value.trim()) return '';

  const lines = value.split('\n');
  const htmlParts = [];
  let paragraphLines = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) return;
    htmlParts.push(`<p>${paragraphLines.join('<br>')}</p>`);
    paragraphLines = [];
  };

  const isStandaloneHtmlLine = (line) => {
    const trimmed = String(line || '').trim();
    if (!trimmed) return false;
    if (!trimmed.startsWith('<') || !trimmed.endsWith('>')) return false;
    return HTML_LIKE_TAG_RE.test(trimmed);
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const trimmed = String(line || '').trim();

    if (!trimmed) {
      flushParagraph();
      continue;
    }

    if (isStandaloneHtmlLine(line)) {
      flushParagraph();
      const codeLines = [trimmed];
      while (i + 1 < lines.length && isStandaloneHtmlLine(lines[i + 1])) {
        i += 1;
        codeLines.push(String(lines[i] || '').trim());
      }
      htmlParts.push(`<pre><code>${escapeHtml(codeLines.join('\n'))}</code></pre>`);
      continue;
    }

    paragraphLines.push(lineToEscapedHtmlWithInlineCodeTags(line));
  }

  flushParagraph();
  return htmlParts.join('');
}

function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Converts markdown-syntax plain text to an HTML string suitable for
 * editor insertion. Handles headings, bullet lists, ordered lists, and
 * paragraph blocks. Inline markdown (bold, italic, code) is preserved as-is
 * and handled by TipTap's own inline parsing on insertion.
 */
export function markdownTextToHtml(text = '') {
  const BULLET_RE = /^[-*•]\s+(.+)$/;
  const ORDERED_RE = /^\d+[.)]\s+(.+)$/;

  const normalized = sanitizeUntrustedText(text).replace(/\n{3,}/g, '\n\n');
  const rawBlocks = normalized.split('\n\n');
  const blocks = rawBlocks
    .map(b => b.split('\n').map(l => l.trimEnd()).filter(l => l.trim()))
    .filter(b => b.length > 0);

  if (!blocks.length) return '';

  // Detect title structure: first block is a single title-like line with no
  // markdown heading prefix, followed by at least one prose block.
  const firstLines = blocks[0];
  const firstLineText = firstLines.length === 1 ? firstLines[0].trim() : '';
  const firstIsTitle = Boolean(
    firstLineText
    && !firstLineText.match(/^#{1,6}\s/)
    && isLikelyStandaloneHeadingLine(firstLineText)
    && blocks.slice(1).some(b => {
      const combined = b.join(' ');
      return combined.length >= 45 || /[.!?]["')\]]?$/.test(combined);
    })
  );

  const htmlParts = [];

  for (let i = 0; i < blocks.length; i++) {
    const lines = blocks[i];

    // Promote first title-like block to h2.
    if (i === 0 && firstIsTitle) {
      htmlParts.push(`<h2>${escapeHtml(lines[0].trim())}</h2>`);
      continue;
    }

    // Single-line markdown heading.
    if (lines.length === 1) {
      const headingMatch = lines[0].trim().match(/^(#{1,6})\s+(.+)$/);
      if (headingMatch) {
        const level = Math.min(headingMatch[1].length, 6);
        htmlParts.push(`<h${level}>${escapeHtml(headingMatch[2].trim())}</h${level}>`);
        continue;
      }
    }

    // Bullet list — all lines must match.
    if (lines.every(l => BULLET_RE.test(l.trim()))) {
      const items = lines
        .map(l => `<li>${escapeHtml(l.trim().replace(BULLET_RE, '$1'))}</li>`)
        .join('');
      htmlParts.push(`<ul>${items}</ul>`);
      continue;
    }

    // Ordered list — all lines must match.
    if (lines.every(l => ORDERED_RE.test(l.trim()))) {
      const items = lines
        .map(l => `<li>${escapeHtml(l.trim().replace(ORDERED_RE, '$1'))}</li>`)
        .join('');
      htmlParts.push(`<ol>${items}</ol>`);
      continue;
    }

    // Paragraph — multi-line blocks use <br> within a single <p>.
    htmlParts.push(`<p>${lines.map(escapeHtml).join('<br>')}</p>`);
  }

  return htmlParts.join('');
}

// --------------- Pipeline entry points ---------------

/**
 * Full HTML formatting pipeline for pasted content.
 * Runs base sanitization then structural normalization passes.
 * Use in transformPastedHTML (TipTap) and manual clipboard paste actions.
 */
export function formatPastedHtml(html = '') {
  let next = sanitizePastedHtml(html);
  if (!CONTENT_FORMATTING_ENABLED) return next;
  // Normalize flat external HTML (Figma etc.) before structural passes.
  next = normalizeExternalFlatHtml(next);
  next = normalizeStyledParagraphsToHeadings(next);
  next = normalizeLeadHeadingFromDoubleBreakParagraph(next);
  next = normalizeDoubleBrInHtml(next);
  next = normalizeListsInHtml(next);
  next = normalizeHeadingsInHtml(next);
  next = stripBoldInsideHeadingTags(next);
  return next;
}

/**
 * Text pipeline for pasted plain text (base normalization only).
 * For markdown-structured plain text, use hasMarkdownStructure() and
 * markdownTextToHtml() in the paste handler to convert and insert as HTML.
 */
export function formatPastedText(text = '') {
  return sanitizePastedText(text);
}

/**
 * Formatting pipeline for AI-generated response text.
 * Normalizes whitespace and provider artifacts before downstream parsing
 * (e.g. markdownToNodes). Use in AI finalization stage.
 */
export function formatAiResponseText(text = '') {
  let next = normalizeAiResponseText(text);
  next = stripBoldMarkersInMarkdownHeadingLines(next);
  next = stripBoldInsideHeadingTags(next);
  next = expandInlineOrderedListMarkers(next);
  return next;
}
